import React, { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setUser, userLoggedOut } from 'store/auth/userSlice'
import { apiSignIn, apiSignOut, apiSignUp, initializeDashboardPresence } from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import { useFirebase } from 'contexts/FirebaseContext'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import NotificationService from 'services/NotificationService'

function useAuth() {
    const dispatch = useDispatch()
    const authorityFixedRef = useRef(false)

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useSelector((state) => state.auth.session)
    const user = useSelector((state) => state.auth.user)
    const { initializeForDashboardUser, updateUserOnlineStatus } = useFirebase()

    // Check if user has old uppercase authority format and fix it (only once)
    React.useEffect(() => {
        if (user && user.authority && user.authority.length > 0 && !authorityFixedRef.current) {
            const hasOldFormat = user.authority.some(auth => auth === 'ADMIN' || auth === 'SUPPORT' || auth === 'USER')
            if (hasOldFormat) {
                console.log('🔍 [AUTH] Detected old authority format, fixing...')
                const fixedAuthority = user.authority.map(auth => {
                    if (auth === 'ADMIN') return 'admin'
                    if (auth === 'SUPPORT') return 'support'
                    if (auth === 'USER') return 'user'
                    return auth
                })

                const fixedUser = {
                    ...user,
                    authority: fixedAuthority
                }

                console.log('🔍 [AUTH] Fixed authority:', fixedAuthority)
                authorityFixedRef.current = true
                dispatch(setUser(fixedUser))
            }
        }
    }, [user, dispatch])

    const signIn = async (values) => {
        try {
            // Map email to key as expected by the API
            const loginData = {
                key: values.email,
                password: values.password,
                rememberMe: values.rememberMe !== undefined ? values.rememberMe : true
            }

            const resp = await apiSignIn(loginData)

            // Handle the new API response structure
            if (resp.data && resp.data.status && resp.data.data) {
                const { token, user } = resp.data.data

                // Check if user has permission to access dashboard
                if (user && user.userType === 'user') {
                    throw new Error('Regular users are not allowed to access the dashboard. Only admin and support staff can login.')
                }

                // Store the token
                dispatch(onSignInSuccess(token))

                // Map user data to the expected format
                let mappedUser = null;
                if (user) {
                    const authority = user.userType === 'admin' ? ['admin'] : user.userType === 'support' ? ['support'] : user.userType === 'shopper' ? ['shopper'] : ['user']

                    mappedUser = {
                        ...user,
                        userName: user.username || user.email || 'User',
                        avatar: user.profile?.avatar || '',
                        authority: authority,
                    }

                    // Debug: Log authority mapping
                    console.log('🔍 [AUTH] User type:', user.userType)
                    console.log('🔍 [AUTH] Mapped authority:', authority)
                    console.log('🔍 [AUTH] Full mapped user:', mappedUser)

                    dispatch(setUser(mappedUser))
                }

                // Initialize Firebase auth and dashboard presence after successful authentication
                if (mappedUser) {
                    try {
                        console.log('🔍 [DASHBOARD AUTH] Initializing Firebase auth and presence...');

                        // Initialize Firebase for this dashboard user
                        await initializeForDashboardUser(
                            mappedUser._id,
                            mappedUser.name || mappedUser.username,
                            mappedUser.userType || 'admin',
                            {
                                name: mappedUser.name || mappedUser.username,
                                email: mappedUser.email,
                                avatar: mappedUser.avatar || mappedUser.profile?.avatar || '',
                                role: mappedUser.userType || 'admin'
                            }
                        );

                        // Initialize global presence for admin/support user
                        await initializeDashboardPresence(mappedUser);

                        // Register device token
                        await NotificationService.registerCurrentDeviceToken('web', {
                            dashboard: true,
                            userType: user?.userType || 'admin'
                        });

                        console.log('✅ [DASHBOARD AUTH] Firebase auth, presence and device token initialized');
                    } catch (initError) {
                        console.log('⚠️ [DASHBOARD AUTH] Failed to initialize Firebase/presence/device token:', initError);
                        // Don't fail login if initialization fails
                    }
                }

                const redirectUrl = query.get(REDIRECT_URL_KEY)

                // Redirect support users to support page, admin users to dashboard
                let finalRedirectPath
                if (user.userType === 'support') {
                    // Support users should go directly to support list, not through admin dashboard
                    // Ignore redirect URL for support users to prevent access denied
                    finalRedirectPath = '/app/sales/support-list'
                } else if (user.userType === 'shopper') {
                    // DAM asset contributors land on mobile capture — the only
                    // area their authority can access.
                    finalRedirectPath = '/app/assets/capture'
                } else {
                    // Admin users can use redirect URL or default to dashboard
                    finalRedirectPath = redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                }

                console.log('🔍 [AUTH] Redirect logic:')
                console.log('🔍 [AUTH] User type:', user.userType)
                console.log('🔍 [AUTH] Redirect URL:', redirectUrl)
                console.log('🔍 [AUTH] Final redirect path:', finalRedirectPath)

                navigate(finalRedirectPath)

                return {
                    status: 'success',
                    message: resp.data.message || 'Login successful',
                }
            } else {
                // Handle case where response doesn't have expected structure
                return {
                    status: 'failed',
                    message: 'Invalid response format',
                }
            }
        } catch (errors) {
            // Enhanced error handling
            let errorMessage = 'Login failed'

            if (errors?.response?.data?.message) {
                errorMessage = errors.response.data.message
            } else if (errors?.response?.data?.error) {
                errorMessage = errors.response.data.error
            } else if (errors?.response?.data?.errors) {
                // Handle validation errors array
                const validationErrors = errors.response.data.errors
                if (Array.isArray(validationErrors) && validationErrors.length > 0) {
                    errorMessage = validationErrors.map(err => err.msg).join(', ')
                }
            } else if (errors?.message) {
                errorMessage = errors.message
            }

            return {
                status: 'failed',
                message: errorMessage,
            }
        }
    }

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            }
                        )
                    )
                }

                // Register device token after successful signup
                try {
                    console.log('🔍 [DASHBOARD AUTH] Registering device token after successful signup...');
                    await NotificationService.registerCurrentDeviceToken('web', {
                        dashboard: true,
                        userType: resp.data.user?.userType || 'user'
                    });
                } catch (deviceTokenError) {
                    console.log('⚠️ [DASHBOARD AUTH] Failed to register device token:', deviceTokenError);
                    // Don't fail signup if device token registration fails
                }

                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        console.log('Clearing stored data...')

        // Unregister device token before clearing data
        try {
            console.log('🔍 [DASHBOARD AUTH] Unregistering device token during sign out...');
            NotificationService.unregisterCurrentDeviceToken();
        } catch (error) {
            console.log('⚠️ [DASHBOARD AUTH] Failed to unregister device token during sign out:', error);
        }

        // Clear any stored tokens or user data
        localStorage.removeItem('admin')
        localStorage.removeItem('pushBannerDismissed')
        sessionStorage.clear()

        console.log('Dispatching logout actions...')
        // Dispatch logout actions
        dispatch(onSignOutSuccess())
        dispatch(userLoggedOut())

        console.log('Navigating to:', appConfig.unAuthenticatedEntryPath)
        // Navigate to sign-in page
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        try {
            console.log('Attempting logout...')

            // Get current user data for logout from Redux store
            const currentUser = user;

            // Update Firebase user offline status
            try {
                console.log('🔍 [DASHBOARD AUTH] Setting Firebase user offline...');
                await updateUserOnlineStatus(currentUser._id, false, currentUser.userType || 'admin');
            } catch (firebaseError) {
                console.log('⚠️ [DASHBOARD AUTH] Failed to update Firebase user offline status:', firebaseError);
                // Continue with logout even if Firebase update fails
            }

            // Unregister device token before making logout API call
            try {
                console.log('🔍 [DASHBOARD AUTH] Unregistering device token before logout API call...');
                await NotificationService.unregisterCurrentDeviceToken();
            } catch (deviceTokenError) {
                console.log('⚠️ [DASHBOARD AUTH] Failed to unregister device token:', deviceTokenError);
                // Continue with logout even if device token unregistration fails
            }

            await apiSignOut({ user: currentUser })
            console.log('Logout API call successful')
        } catch (error) {
            console.log('Logout API error:', error)
            // Continue with logout even if API call fails
        } finally {
            console.log('Handling sign out...')
            handleSignOut()
        }
    }

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        signOut,
    }
}

export default useAuth
